/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useMemo } from 'react';

import { useParams } from 'js/lib/useRouter';

import { Grid, Typography2, breakpoints } from '@coursera/cds-core';

import type { CoreCollectionEntityWithMetadata } from 'bundles/collections-carousel/types/CarouselCollection';
import { mapCollectionEntityForCarousel } from 'bundles/collections-carousel/utils/mappingUtils';
import CollectionModule from 'bundles/page-config-common/components/shared/CollectionModule';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import type { PageConfig_ComponentConfig_PageConfig_EnterpriseBrowseCollectionComponent_Fragment as CollectionComponent } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';
import { ENTERPRISE_BROWSE_COLLECTION_LIMIT } from 'bundles/unified-home-common/constants';

const styles = {
  enterpriseCollectionHeader: css`
    margin-top: var(--cds-spacing-150);
  `,
  collectionSpacing: css`
    gap: var(--cds-spacing-400);

    ${breakpoints.down('sm')} {
      gap: var(--cds-spacing-300);
    }
  `,
};

const FREE_COLLECTION_IDS = ['LmyL-2WZTmeRq-rWx2tDVw'];

const EnterpriseBrowseCollectionModuleRenderer = ({
  pageConfigComponent,
}: {
  pageConfigComponent: CollectionComponent;
}) => {
  const { programSlug } = useParams();
  const { currentLevel, nextLevel } = useHeaderLevel();
  const carouselCollections = useMemo(() => {
    return (
      pageConfigComponent.collection?.enterpriseBrowseCollectionEntities?.elements
        // Filter out "free" collections.
        ?.filter(
          (collection) =>
            collection?.__typename === 'Collection_Collection' && !FREE_COLLECTION_IDS.includes(collection.id)
        )
        ?.slice(0, ENTERPRISE_BROWSE_COLLECTION_LIMIT)
        ?.map((maybeCollection, index) => {
          if (maybeCollection?.__typename === 'Collection_Collection') {
            return mapCollectionEntityForCarousel(
              maybeCollection?.id,
              {
                ...maybeCollection,
                metadata: {
                  isEnterprise: true,
                  programSlug,
                  titleComponent: nextLevel,
                },
              } as CoreCollectionEntityWithMetadata,
              {
                collectionIndex: index,
              }
            );
          } else {
            return null;
          }
        })
        ?.filter(Boolean)
    );
  }, [pageConfigComponent, programSlug, nextLevel]);

  if (!carouselCollections || !carouselCollections.length) {
    return null;
  }

  return (
    <HomeSection className="PageConfig_EnterpriseBrowseCollectionComponent" dataTestId="enterprise-browse-collections">
      <Grid container css={styles.collectionSpacing}>
        {carouselCollections?.map((browseCollection) => (
          <Grid item xs={12} key={browseCollection.collectionId}>
            <CollectionModule
              collection={browseCollection}
              disableNegativeMargins={true}
              disableCollectionPadding={true}
              customHeader={
                <div>
                  <Typography2 variant="subtitleLarge" component={currentLevel}>
                    {browseCollection.title}
                  </Typography2>
                </div>
              }
            />
          </Grid>
        ))}
      </Grid>
    </HomeSection>
  );
};

export default EnterpriseBrowseCollectionModuleRenderer;
